import { useToast } from '@/components/ui/Toast'
import i18n from '@/lib/i18n'
import { queryKeys } from '@/lib/queryClient'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '../api/authApi'

/**
 * Hook for managing user profile updates
 * Returns field errors to be displayed under inputs
 */
export function useUpdateProfile() {
  const toast = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (response) => {
      // Update the user data in cache (response format: {success: true, data: {...user...}})
      queryClient.setQueryData(queryKeys.auth.me, response)
      toast.success({
        title: i18n.t('profile.profileUpdated'),
        description: i18n.t('profile.profileUpdatedDesc'),
      })
    },
    onError: (error) => {
      // Don't show field errors in toast, they'll be shown under inputs
      if (!error.error?.details) {
        const message = error.message || i18n.t('profile.profileUpdateError')
        toast.error({
          title: i18n.t('profile.profileUpdateError'),
          description: message,
        })
      }
      // Re-throw error with details so component can display them
      throw error
    },
  })
}

/**
 * Hook for changing password
 * Returns field errors to be displayed under inputs
 */
export function useChangePassword() {
  const toast = useToast()

  return useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      toast.success({
        title: i18n.t('profile.passwordChanged'),
        description: i18n.t('profile.passwordChangedDesc'),
      })
    },
    onError: (error) => {
      // Don't show field errors in toast, they'll be shown under inputs
      if (!error.error?.details) {
        const message = error.message || i18n.t('profile.passwordChangeError')
        toast.error({
          title: i18n.t('profile.passwordChangeError'),
          description: message,
        })
      }
      // Re-throw error with details so component can display them
      throw error
    },
  })
}
